import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from "assets/images/logoSvg.svg";
import { END_POINT } from "config/environment";
import { formatJobYearsLabel } from "../utils/formatJobYearsLabel";
import { formatDate } from "../../../utils/formHelpers/formatExpiryDate";

export default function CareerPublicPortal() {
  const navigate = useNavigate();

  const [jobs, setJobs] = useState<any[]>([]);
  const [jobTitle, setJobTitle] = useState("");
  const [jobType, setJobType] = useState("");
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(true);
  const [filteredJobs, setFilteredJobs] = useState<any[]>([]);
  const [selectedDescription, setSelectedDescription] = useState<string | null>(
    null
  );

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(`${END_POINT.BASE_URL}/career-portal`);
        const allJobs = response.data?.data || response.data || [];
        setJobs(allJobs);
        setFilteredJobs(allJobs);
      } catch (error) {
        console.error("Error fetching jobs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  useEffect(() => {
    if (selectedDescription) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedDescription]);

  const handleSearch = () => {
    const filtered = jobs.filter((job: any) => {
      const matchesTitle = jobTitle
        ? job.title?.toLowerCase().includes(jobTitle.toLowerCase())
        : true;
      const matchesType = jobType
        ? job.title?.toLowerCase().includes(jobType.toLowerCase())
        : true;
      const matchesLocation = location
        ? job.location?.toLowerCase().includes(location.toLowerCase())
        : true;
      return matchesTitle && matchesType && matchesLocation;
    });

    setFilteredJobs(filtered);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <header className="bg-white shadow-sm py-4 px-6 flex items-center gap-3 sticky top-0 z-50">
        <a
          href="https://www.arnopro.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img src={logo} alt="logo" className="h-8" />
        </a>
      </header>

      <section className="relative h-[50vh] w-full bg-primary flex items-center justify-center">
        <video
          src="/assets/authVideo.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="absolute w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative text-center text-white z-10">
          <h1 className="text-4xl font-bold mb-2 text-white">
            Discover live vacancies sourced from organisations actively
            recruiting through Arno
          </h1>
          {/* <p className="text-lg">
            Browse opportunities from companies actively hiring on Arno
          </p> */}
        </div>
      </section>

      <div className="bg-white rounded-lg shadow-md p-6 max-w-8xl mx-auto z-20 relative">
        <h2 className="text-2xl font-semibold text-center mb-6 text-primary">
          Career Opportunities
        </h2>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input
              type="text"
              placeholder="Job Title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              className="border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
            />

            <select
              value={jobType}
              onChange={(e) => setJobType(e.target.value)}
              className="border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            >
              <option value="">Select Job Type</option>
              <option value="Contract">Contract</option>
              <option value="Full Time">Full Time</option>
              <option value="Part Time">Part Time</option>
              <option value="Internship">Internship</option>
              <option value="Consultancy">Consultancy</option>
              <option value="Freelance">Freelance</option>
            </select>

            <input
              type="text"
              placeholder="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="border rounded-lg px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="mt-4 flex justify-end gap-4">
            <button
              onClick={handleSearch}
              className="bg-primary text-white rounded-lg px-4 py-2 font-semibold hover:bg-primary/90 transition w-auto"
              disabled={loading}
            >
              {loading ? "Loading..." : "Search for Jobs"}
            </button>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-gray-500">Fetching jobs...</p>
        ) : filteredJobs.length === 0 ? (
          <p className="text-center text-gray-500">No jobs found.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredJobs.map((job: any, index: number) => (
              <div
                key={index}
                className="border rounded-xl shadow-md bg-white p-6 hover:shadow-lg transition-all duration-300"
              >
                <div className="flex justify-between items-start mb-3">
                  <h2 className="text-xl font-semibold text-primary">
                    {job.title || "Untitled Job"}
                  </h2>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/sign-in/talent");
                    }}
                    className="text-sm border border-primary text-primary bg-white px-4 py-2 rounded-full font-medium transition-all duration-200 hover:bg-primary hover:text-white"
                  >
                    View Job
                  </a>
                </div>

                <p className="text-gray-700 font-medium mb-1">
                  {job.company_name || "Unknown Company"}
                </p>

                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-600 mb-4">
                  <p>
                    <strong>Job Type:</strong>{" "}
                    {job.employment_type || "Not specified"}
                  </p>
                  <p>
                    <strong>Level:</strong>{" "}
                    {job.level?.replace("_", " ") || "Not specified"}
                  </p>
                  <p>
                    <strong>Location:</strong>{" "}
                    {`${job.location_city || ""}${
                      job.location_city && job.location_country ? ", " : ""
                    }${job.location_country || ""}`}
                  </p>
                  <p>
                    <strong>Experience:</strong>{" "}
                    {job.years_of_experience
                      ? formatJobYearsLabel(job.years_of_experience)
                      : "Not specified"}
                  </p>
                  <p>
                    <strong>Expiry Date:</strong>{" "}
                    {job.expiry_date
                      ? formatDate(job.expiry_date)
                      : "Not specified"}
                  </p>
                </div>

                <div
                  className="text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none mb-3 line-clamp-6"
                  dangerouslySetInnerHTML={{
                    __html:
                      job.description ||
                      "<p class='text-gray-500'>No description provided.</p>",
                  }}
                />

                <button
                  onClick={() => navigate(`/career-portal/${job.slug}`)}
                  className="text-primary text-sm font-medium hover:underline"
                >
                  Read More
                </button>

                {/* {job.recruiter && (
                  <p className="text-xs text-gray-500 mt-2">
                    Posted by{" "}
                    <span className="font-medium">
                      {job.recruiter.first_name} {job.recruiter.last_name}
                    </span>
                  </p>
                )} */}
              </div>
            ))}
          </div>
        )}

        {selectedDescription && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setSelectedDescription(null)}
          >
            <div
              className="bg-white rounded-xl shadow-lg max-w-3xl w-full mx-4 p-6 overflow-y-auto max-h-[80vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-primary">
                  Full Description
                </h2>
                <button
                  onClick={() => setSelectedDescription(null)}
                  className="text-gray-500 hover:text-red-500 text-2xl leading-none"
                >
                  &times;
                </button>
              </div>

              <div
                className="prose prose-sm max-w-none text-gray-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selectedDescription }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
